import React from 'react';

import {
  Chip,
  Container,
  Grid,
  Icon,
  Link,
  Typography,
  Paper,
} from '@material-ui/core';
import {
  GitHub as GitHubIcon,
  Storage as StorageIcon,
  YouTube as YouTubeIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { Link as ReactLink } from 'react-router-dom';

import Header from '../components/Headers';
import Title from '../components/Title';
import Author from '../components/Author';
import Banner from '../components/Banner';
import Footer from '../components/Footer';

const useStyles = makeStyles((theme) => ({
  main: {
    textAlign: 'center',
  },
  titleHead: {
    paddingTop: theme.spacing(5),
    fontFamily: 'Source Serif Pro',
    '& > a > *': {
      margin: theme.spacing(0.5),
    },
  },
  bibtexSpan: {
    backgroundColor: theme.palette.grey[200],
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 4),
  },
  iconText: {
    wordWrap: 'break-word',
  },
  img: {
    margin: 'auto',
    display: 'block',
    maxWidth: '100%',
    maxHeight: '100%',
  },
}));

export default function MainPage(): React.ReactElement {
  const classes = useStyles();
  const mainPageSections = [
    { title: 'Home', url: '#' },
    { title: 'Abstract', url: '#abstract' },
    { title: 'Paper', url: '#paper' },
    { title: 'Download', url: '#download' },
    // { title: 'Results', url: '#results' },
  ];
  const authorName = 'Hung-Jin Lin';
  const authorGithubProfile = 'https://github.com/levirve';
  const githubPage = 'https://github.com/leVirve/lsun-room';
  const paperName = 'Indoor Scene Layout Estimation from a Single Image';

  return (
    <div className={classes.main}>
      <Header
        title={paperName}
        githubPage={githubPage}
        sections={mainPageSections}
      />
      <Container maxWidth="lg">
        <HeadingTeaser />
        <AbstractSection />
        <DownloadSection />
        {/* <ResultsSection /> */}
        <PaperSection />
        <AcknowledgmentsSection />
      </Container>
      <Footer authorName={authorName} githubPage={authorGithubProfile} />
    </div>
  );
}

function HeadingTeaser(): React.ReactElement {
  const classes = useStyles();
  const topicLabels = [
    { text: 'ICPR 2018', url: 'https://iapr.org/archives/icpr2018/' },
    { text: 'Room Layout', url: 'https://iapr.org/archives/icpr2018/' },
  ];
  const imageUrl = `${process.env.PUBLIC_URL}/images/teaser.png`;

  return (
    <>
      <Typography
        component="h1"
        variant="h3"
        gutterBottom
        className={classes.titleHead}
      >
        Indoor Scene Layout Estimation <br />
        from a Single Image <br />
        {topicLabels.map((label) => (
          <Link
            key={label.text}
            href={label.url}
            target="_blank"
            rel="noopener"
          >
            <Chip label={label.text} />
          </Link>
        ))}
      </Typography>
      <AuthorSection />
      <Banner elevation={0} imageSrc={imageUrl} />
    </>
  );
}

function AuthorSection(): React.ReactElement {
  const authors = [
    {
      name: 'Hung-Jin Lin',
      url: 'https://tw.linkedin.com/in/hung-jin-lin-5b66119b',
      affiliation: '1',
    },
    {
      name: 'Sheng-Wei Huang',
      url: 'https://www.linkedin.com/in/sheng-wei-huang',
      affiliation: '1',
    },
    {
      name: 'Shang-Hong Lai',
      url: 'https://tw.linkedin.com/in/shang-hong-lai-4913a0b',
      affiliation: '1',
    },
    {
      name: 'Chen-Kuo Chiang',
      url: 'https://www.cs.ccu.edu.tw/~ckchiang',
      affiliation: '2',
    },
  ];

  const affiliations = [
    { number: '1', name: 'National Tsing Hua University', url: '' },
    { number: '2', name: 'National Chung Cheng University', url: '' },
  ];
  return (
    <>
      <Author authors={authors} affiliations={affiliations} />
    </>
  );
}

function AbstractSection(): React.ReactElement {
  const classes = useStyles();
  return (
    <>
      <Title anchor="overview" name="Overview" />
      <Typography component="h3" variant="h6" align="left" paragraph>
        Unlike previous deep learning-based methods that depend on
        post-processing refinement (e.g., proposal ranking and optimization). We
        propose a deep learning-based approach for estimating the layout of a
        given indoor image in real-time. Our method consists of a fully
        convolutional network, a novel{' '}
        <a href="#method-layout-degrade">layout-degeneration</a> augmentation
        method to mitigate the dataset imbalance, and a new training pipeline
        which integrate an adaptive edge penalty and smoothness terms into the
        training process without deploying postprocessing techniques.
      </Typography>

      <Title
        anchor="method-layout-degrade"
        name="Layout Degradation Augmentation"
        fontVariant={{ variant: 'h5' }}
      />
      <Grid container justify="center">
        <Grid item>
          <img
            src={`${process.env.PUBLIC_URL}/images/degrade_graph.png`}
            alt="degrade_graph"
            className={classes.img}
          />
        </Grid>
        <Grid item>
          <img
            src={`${process.env.PUBLIC_URL}/images/degrade_dataset.png`}
            alt="degrade_graph"
            className={classes.img}
          />
        </Grid>
      </Grid>

      <Title anchor="abstract" name="Abstract" />
      <Typography component="h3" variant="h6" align="left" paragraph>
        With the popularity of the hand devices and intelligent agents, many
        aimed to explore machine’s potential in interacting with reality. Scene
        understanding, among the many facets of reality interaction, has gained
        much attention for its relevance in applications such as augmented
        reality (AR). Scene understanding can be partitioned into several
        subtasks (i.e., layout estimation, scene classification, saliency
        prediction, etc). In this paper, we propose a deep learning-based
        approach for estimating the layout of a given indoor image in real-time.
        Our method consists of a deep fully convolutional network, a novel
        layout-degeneration augmentation method, and a new training pipeline
        which integrate an adaptive edge penalty and smoothness terms into the
        training process. Unlike previous deep learning-based methods that
        depend on post-processing refinement (e.g., proposal ranking and
        optimization), our method motivates the generalization ability of the
        network and the smoothness of estimated layout edges without deploying
        postprocessing techniques. Moreover, the proposed approach is
        time-efficient since it only takes the model one forward pass to render
        accurate layouts. We evaluate our method on LSUN Room Layout and Hedau
        dataset and obtain estimation results comparable with the
        state-of-the-art methods.
      </Typography>
    </>
  );
}

function PaperSection(): React.ReactElement {
  const classes = useStyles();
  const thumbImageUrl = `${process.env.PUBLIC_URL}/images/thumb.jpg`;
  const thumbTitle = `Indoor Scene Layout Estimation from a Single Image, ICPR 2018 (IEEE Xplore)`;
  const ieeeLink = 'https://ieeexplore.ieee.org/document/8546278';
  const descriptionText = `Hung-Jin Lin, Sheng-Wei Huang, Shang-Hong Lai, and Chen-Kuo Chiang, "Indoor Scene Layout Estimation from a Single Image", in Proceedings
  of the International Conference on Pattern Recognition (ICPR), 2018`;
  const bibtex = `@inproceedings{lin2018layoutestimation,
    Author = {Hung Jin Lin and Sheng-Wei Huang and Shang-Hong Lai and Chen-Kuo Chiang},
    Title = {Indoor Scene Layout Estimation from a Single Image},
    Booktitle = {2018 24th International Conference on Pattern Recognition (ICPR)},
    Year = {2018}
}`;

  return (
    <>
      <Title anchor="paper" name="Paper" />
      <Chip label="IEEE Xplore" variant="outlined" color="primary" />
      <Grid item>
        <Link href={ieeeLink} target="_blank" rel="noopener">
          <Banner title={thumbTitle} elevation={0} imageSrc={thumbImageUrl} />
        </Link>
        <Typography align="left" variant="h6" color="inherit" gutterBottom>
          Public PDF version is also provided in{' '}
          <a href="#download">downloadable section</a>.
        </Typography>
      </Grid>
      <Title anchor="citation" name="Citation" />
      <Typography align="left" variant="h6" color="inherit" gutterBottom>
        {descriptionText}
      </Typography>
      <Chip label="BibTeX" variant="outlined" color="primary" />
      <Paper elevation={0} className={classes.bibtexSpan}>
        <Typography align="left" variant="h6" color="inherit" gutterBottom>
          <pre style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
            {bibtex}
          </pre>
        </Typography>
      </Paper>
    </>
  );
}

function DownloadSection(): React.ReactElement {
  const classes = useStyles();
  const youtubeLink = '';
  const githubProjectLink = 'https://github.com/levirve/lsun-room';
  const pdfLink =
    'https://github.com/leVirve/lsun-room/blob/master/doc/icpr2018_lin_layoutestimation.pdf';
  const demoZipLink = '';

  const iconLinksContent = [
    { name: 'PDF', url: pdfLink, icon: PictureAsPdfIcon },
    { name: 'Code', url: githubProjectLink, icon: GitHubIcon },
    { name: 'Video', url: youtubeLink, icon: YouTubeIcon },
    { name: 'Demo Results', url: demoZipLink, icon: StorageIcon },
  ];

  return (
    <>
      <Title anchor="download" name="Downloadable" />
      <Grid container justify="center" spacing={1}>
        {iconLinksContent.map((iconLink) => (
          <Grid item xs={2} key={iconLink.url}>
            <Link href={iconLink.url} target="_blank" rel="noopener">
              <Icon
                component={iconLink.icon}
                color="action"
                style={{ fontSize: 50 }}
              />
              <Typography
                align="center"
                variant="h6"
                color="inherit"
                className={classes.iconText}
              >
                {iconLink.name}
              </Typography>
            </Link>
          </Grid>
        ))}
      </Grid>
    </>
  );
}

function ResultsSection(): React.ReactElement {
  return (
    <>
      <Title anchor="results" name="Results" />
      <div>
        <ReactLink to="/demo">
          <Typography variant="h6" color="inherit" gutterBottom>
            <Banner
              elevation={0}
              imageSrc={`${process.env.PUBLIC_URL}/images/style_inter.gif`}
            />
            {/* a dummy link stuff */}
            {/* <Link href="/demo" target="_blank" rel="noopener">
              Demo Website
            </Link> */}
          </Typography>
        </ReactLink>
      </div>
    </>
  );
}

function AcknowledgmentsSection(): React.ReactElement {
  const referenceLinks = [
    {
      title: 'Recovering the spatial layout of cluttered rooms',
      link:
        'http://dhoiem.web.engr.illinois.edu/publications/iccv2009_hedau_indoor.pdf',
      suffix: 'Varsha Hedau, Derek Hoiem, David Forsyth, ICCV 2009',
    },
    {
      title:
        'Learning Informative Edge Maps for Indoor Scene Layout Prediction',
      link:
        'https://slazebni.cs.illinois.edu/publications/iccv15_informative.pdf',
      suffix: 'Arun Mallya, Svetlana Lazebnik, ICCV 2015',
    },
    {
      title: 'A Coarse-to-Fine Indoor Layout Estimation (CFILE) Method',
      link: 'https://arxiv.org/abs/1607.00598',
      suffix: 'Yuzhuo Ren, Shangwen Li, Chen Chen, C.-C. Jay Kuo, ACCV 2016',
    },
    {
      title:
        'Learning to predict high quality edge maps for room layout estimation',
      link: 'https://ieeexplore.ieee.org/document/7792744',
      suffix:
        'Weidong Zhang, Wei Zhang, Kan Liu, Jason Gu, IEEE Trans Multimedia 2017',
    },
    {
      title:
        'Delay: Robust spatial layout estimation for cluttered indoor scenes',
      link: 'http://svl.stanford.edu/assets/papers/delay-robust-spatial.pdf',
      suffix:
        'Saumitro Dasgupta, Kuan Fang, Kevin Chen, Silvio Savarese, CVPR 2016',
    },
    {
      title:
        'Physics Inspired Optimization on Semantic Transfer Features: An Alternative Method for Room Layout Estimation',
      link: 'https://sites.google.com/view/st-pio/',
      suffix:
        'Hao Zhao, Ming Lu, Anbang Yao, Yiwen Guo, Yurong Chen, Li Zhang, CVPR 2017',
    },
  ];

  return (
    <>
      <Title anchor="acknowledgments" name="References" />
      <Typography variant="body1" align="left" paragraph>
        We thank{' '}
        <Link href="http://lsun.cs.princeton.edu/2015.html#layout">
          LSUN Room 2016 Challenge,{' '}
        </Link>{' '}
        and{' '}
        <Link href="https://github.com/yuzhuoren/IndoorLayout">Yuzhuo Ren</Link>{' '}
        for providing datasets. <br />
        {referenceLinks.map((reference, index) => (
          <>
            [{index + 1}] <Link href={reference.link}>{reference.title}</Link>,{' '}
            {reference.suffix}. <br />
          </>
        ))}
      </Typography>
    </>
  );
}
